import _ from "lodash";
import { normalize } from '../../../../components/interop/utils';
import ACCENT_WORDS from './accentWords.json'; // built by scripts/content/manual-text-layers/buildAccentWords.js

const areClose = (a,b,scale) => {
  return Math.abs(a-b)/scale < 0.05
}

function generateGlobalStyles(styleStats) {
  if (_.isEmpty(styleStats)) {
    return {};
  }

  let topStyles = _.sortBy(_.toPairs(styleStats), '1.charCount').reverse();

  let colors = ['#333', '#0000a0', '#006d00', '#7e36aa', '#167f90'];
  let globalStyles = {}

  let totalChars = _.sum(_.map(topStyles, ([key, stats]) => stats.charCount));

  let top = topStyles[0][1];
  // If there is no global dominant style, probabbly same styles have same name
  let noGlobalStyle = top.charCount / totalChars < 0.3;

  _.each(topStyles, ([styleKey, { avgCharWidth, charCount }], i) => {
    let color = '#af4795';
    if (noGlobalStyle) {
      color = (charCount / totalChars > 0.01) ? 'black' : colors[i % (colors.length - 1) + 1];
    } else if (colors[i]) {
      color = colors[i];
    }

    globalStyles[styleKey] = { color };

    if (avgCharWidth > 0.5) {
      globalStyles[styleKey].fontWeight = '700';

      if (avgCharWidth > 0.535) {
        globalStyles[styleKey].fontFamily = '"Montserrat"';
      }
    } else if (avgCharWidth < 0.42) {
      globalStyles[styleKey].fontFamily = '"Roboto Condensed"';
    }
  });

  if (topStyles.length > 3) {
    let [[s1, normalText], [s2, top2], [s3, top3]] = topStyles;

    globalStyles[s2].color = top2.avgFontSize < normalText.avgFontSize ** 0.97 ? "gray" : globalStyles[s2].color;
    globalStyles[s3].color = top3.avgFontSize < normalText.avgFontSize * 0.97 ? "gray" : globalStyles[s3].color;
  }


  // console.log(_.map(topStyles, ([key,stats]) => {
  //   return `[${key}] ${(stats.charCount/totalChars*100).toFixed(1)}% FontSize ${stats.avgFontSize.toFixed(1)}px Width ${(stats.avgCharWidth*100).toFixed(1)}`
  // }).join(`\n`))

  return globalStyles;
}

// The letter pairs most common in our es/en/pt/fr/de/it locale strings (together ~58% of all their letter pairs)
const COMMON_BIGRAMS = new Set('on de en es ar er re st co ma an ro le in ta te el nt em as tr to se un ng ra ri pr ca or mo ou ti os me io la ic il is pa ne si na om li al us di ai ve ch it nd sp at ut he od ie'.split(' '));
const USUAL_PUNCTUATION = ' .,;:()-/\'"!?%°•–—\u00a0';
const FONT_SAMPLE_CHARS = 3000;

// How much a text looks like real text: share of letter pairs that are common bigrams (and how many distinct ones, so
// that ". . . ." shifted into "esesese" doesn't count), and share of chars that are neither letters, digits nor usual
// punctuation
function textLikeness(text) {
  const t = text.toLowerCase();
  const seen = new Set();
  let pairs = 0, hits = 0, bad = 0, prev = '';
  for (let i = 0; i < t.length; i++) {
    const c = t[i];
    const letter = (c >= 'a' && c <= 'z') || (c >= 'à' && c <= 'ÿ');
    if (letter && prev) {
      pairs++;
      if (COMMON_BIGRAMS.has(prev + c)) {
        hits++;
        seen.add(prev + c);
      }
    }
    prev = letter ? c : '';
    if (!letter && !(c >= '0' && c <= '9') && !USUAL_PUNCTUATION.includes(c) && !/\s/.test(c)) bad++;
  }
  return { bigrams: pairs ? hits / pairs : 0, distinct: seen.size, bad: bad / (t.length || 1), pairs };
}

// Only real spaces are exempt: in these fonts "\u000b" and "\f" are glyphs too (e.g. the parentheses of a +29 font)
const shiftText = (text, offset, spaceCode = -1) => text.replace(/[^ ]/g, (c) => {
  const code = c.charCodeAt(0);
  return code === spaceCode ? ' ' : String.fromCharCode(code + offset);
});

// The first chars of each font's text, to decide how the font is encoded
function collectFontSamples(pages) {
  const samples = {};
  for (const page of pages) {
    for (const { textContent, style } of page.pageItems) {
      const sample = samples[style.fontName] || '';
      if (sample.length < FONT_SAMPLE_CHARS) samples[style.fontName] = sample + textContent;
    }
  }
  return samples;
}

// Some PDFs' fonts emit glyph ids instead of chars, so every char of that font is off by one constant (e.g. "5HWURYLVRUHV"
// is "Retrovisores" + 29). Find that constant per font: try the offsets that would map its most frequent glyphs to
// ' ', 'e' or 'a' (plus the ones already found for other fonts of the document) and keep the one that makes the font's
// text look most like real text, when it clearly beats leaving it as is.
function detectFontOffsets(samples) {
  const offsets = {};
  const found = new Set();

  const decide = (fontName, sample, candidates, minPairs, minDistinct, minBigrams) => {
    // A shift wins when it makes the text clearly more word-like, or when the words already looked fine (upper/lower
    // case swapped fonts read the same) but it gets rid of the control chars
    const raw = textLikeness(sample);
    if (raw.bigrams >= 0.45 && raw.bad <= 0.02) return; // already clean text, nothing could beat it
    let best = null;
    for (const offset of candidates) {
      const l = textLikeness(shiftText(sample, offset));
      const better = l.bigrams >= raw.bigrams + 0.2 || (l.bigrams >= raw.bigrams - 0.05 && l.bad <= raw.bad - 0.15);
      if (l.pairs >= minPairs && l.distinct >= minDistinct && l.bad <= 0.25 && l.bigrams >= minBigrams && better && (!best || l.bigrams > best.bigrams)) {
        best = { offset, ...l };
      }
    }
    if (best) {
      // The font's space is one of its most frequent glyphs; when the shift doesn't land it on something sensible
      // ("\u0087" + 29 = "¤") make it a space
      const spaceCode = _.take(topChars(sample), 6).find(code => textLikeness(String.fromCharCode(code + best.offset)).bad);
      offsets[fontName] = { offset: best.offset, spaceCode: spaceCode === undefined ? -1 : spaceCode };
      found.add(best.offset);
    }
  };

  _.each(samples, (sample, fontName) => {
    if (sample.replace(/\s/g, '').length < 30) return;
    const codes = _.take(topChars(sample), 6);
    decide(fontName, sample, _.uniq([..._.flatMap(codes, c => [32 - c, 101 - c, 97 - c]), ...found]).filter(o => o !== 0), 20, 8, 0.4);
  });
  // Fonts left undecided (too little text, or a few all-caps headings, which score low on bigrams) still get an offset
  // the document's other fonts proved right, if it makes their text read plausibly
  if (found.size) {
    _.each(samples, (sample, fontName) => {
      if (!offsets[fontName]) decide(fontName, sample, [...found], 4, 2, 0.25);
    });
  }
  return offsets;
}

// Char codes of a text by decreasing frequency, whitespace excluded
function topChars(text) {
  const freq = {};
  for (const c of text) {
    if (!/\s/.test(c)) freq[c] = (freq[c] || 0) + 1;
  }
  return _.sortBy(_.toPairs(freq), '1').reverse().map(([c]) => c.charCodeAt(0));
}

const NON_ASCII = /[\u0080-\uffff]/;
const NON_ASCII_ALL = /[\u0080-\uffff]/g;
const ACCENT_EVIDENCE_WORDS = 300; // per unknown char
const ACCENT_EVIDENCE_ITEMS = 5000; // per document

// Glyph-id fonts keep their accented letters wherever the font's glyph order has them, so after the constant shift
// those land on arbitrary chars (mostly C1 controls: "vehculo", "informacin"). The mapping is consistent within a
// document, so recover it: for each such char, the accented letter that turns most of the words containing it into
// known words wins. Only offset fonts' words holding exactly one unknown char are looked at.
function recoverAccents(pages, offsets, lang) {
  const dictionaries = ACCENT_WORDS[lang] ? [ACCENT_WORDS[lang]] : _.values(ACCENT_WORDS);
  const known = new Set(_.flatten(dictionaries));
  const letters = _.uniq(_.flatMap([...known], w => w.match(NON_ASCII_ALL)));

  // Evidence saturates quickly, so only the first few thousand items with such chars are looked at
  const evidence = {};
  let budget = ACCENT_EVIDENCE_ITEMS;
  for (const page of pages) {
    for (const { textContent, style } of page.pageItems) {
      if (!offsets[style.fontName] || !NON_ASCII.test(textContent)) continue;
      if (budget-- <= 0) break;
      for (const [word] of textContent.toLowerCase().matchAll(/[a-z\u0080-\uffff]{3,}/g)) {
        const unknown = word.match(NON_ASCII_ALL);
        if (!unknown || unknown.length !== 1) continue;
        const e = evidence[unknown[0]] || (evidence[unknown[0]] = { count: 0, words: {} });
        if (e.count++ < ACCENT_EVIDENCE_WORDS) e.words[word] = (e.words[word] || 0) + 1;
      }
    }
    if (budget <= 0) break;
  }
  _.each(evidence, (e, ch) => { if (e.count < 3) delete evidence[ch]; });

  const mapping = {};
  const decide = (ch, words, candidates, dictionary) => {
    let best = null, second = 0;
    for (const letter of candidates) {
      let hits = 0;
      for (const word in words) {
        if (dictionary.has(word.replace(ch, letter))) hits += words[word];
      }
      if (!best || hits > best.hits) {
        second = best ? best.hits : 0;
        best = { letter, hits };
      } else if (hits > second) {
        second = hits;
      }
    }
    if (best && best.hits >= 3 && best.hits >= 2 * second) mapping[ch] = best.letter;
  };
  _.each(evidence, ({ words }, ch) => decide(ch, words, letters, known));

  // What the tiny dictionary can't tell (mostly the uppercase glyphs: "PRECAUCIìN") the document's own words can,
  // once resolved: "precaución" is a known word now
  const resolved = new Set();
  _.each(evidence, ({ words }, ch) => {
    if (mapping[ch]) _.each(words, (n, word) => resolved.add(word.replace(ch, mapping[ch])));
  });
  _.each(evidence, ({ words }, ch) => {
    if (mapping[ch]) return;
    decide(ch, words, _.uniq(_.values(mapping)), resolved);
    // A glyph only ever seen at the end of words is the end-of-line hyphen
    if (!mapping[ch] && _.sum(_.values(words)) >= 5 && _.every(words, (n, word) => word.endsWith(ch))) mapping[ch] = '-';
  });
  return mapping;
}

const applyAccents = (text, mapping) => text.replace(NON_ASCII_ALL, (c, i) => {
  const letter = mapping[c.toLowerCase()];
  if (!letter) return c;
  const around = (text[i - 1] || '') + (text[i + 1] || '');
  return /[A-Z]/.test(around) && !/[a-z]/.test(around) ? letter.toUpperCase() : letter;
});

// Fake bold: some PDFs draw every glyph 2-4 times, which comes out as repeated chars ("LL", "RRRREEEE") and/or as
// overlapping items with the same text. Returns the multiplier of the fonts that do it.
function detectMultipliedGlyphs(samples) {
  const multipliers = {};
  _.each(samples, (sample, fontName) => {
    let letters = 0, repeated = 0;
    const distinct = new Set();
    for (let i = 0; i < sample.length; i++) {
      if (!/[0-9A-Za-zÀ-ÿ]/.test(sample[i])) continue;
      letters++;
      distinct.add(sample[i]);
      if (sample[i] === sample[i - 1]) repeated++;
    }
    // (some PDFs use one font per glyph: a font that is only "llll" is not fake bold)
    const share = letters >= 30 && distinct.size >= 5 ? repeated / letters : 0;
    if (share >= 0.45) multipliers[fontName] = Math.round(1 / (1 - share));
  });
  return multipliers;
}

// Whether the next item continues this one with no space in between, along whichever direction the text runs: left to
// right, right to left (rotated 180°) or, for one-glyph items, vertically (width is the advance in the text direction)
function isGlued(item, next) {
  const a = item.style, b = next.style;
  if (!areClose(a.height, b.height, a.height)) return false;
  const along = (x, y) => areClose(x, y, a.height);
  const across = (x, y) => Math.abs(x - y) < a.height * 0.25;
  if (across(a.top, b.top) && (along(a.left + a.width, b.left) || along(b.left + b.width, a.left))) return true;
  return item.textContent.length === 1 && next.textContent.length === 1
    && across(a.left, b.left) && (along(a.top - a.width, b.top) || along(a.top + a.width, b.top));
}

// Dot leaders whose glyph has no unicode ("\b\b\b\b\b", "\ufffd\ufffd\ufffd")
const LEADER_RUNS = /([\u0000-\u001f\ufffd])\1{2,}/g;

function postProcessText(pages, lang) {
  const samples = collectFontSamples(pages);
  const offsets = detectFontOffsets(samples);
  const multipliers = detectMultipliedGlyphs(samples);

  for (const page of pages) {
    let prev = null;
    for (const item of page.pageItems) {
      const { fontName, left, top, height } = item.style;
      const shift = offsets[fontName];
      if (shift) item.textContent = shiftText(item.textContent, shift.offset, shift.spaceCode);
      const k = multipliers[fontName];
      if (k) item.textContent = item.textContent.replace(/(.)\1+/g, run => run[0].repeat(Math.max(1, Math.round(run.length / k))));
      item.textContent = item.textContent.replace(LEADER_RUNS, run => '.'.repeat(run.length));

      // Items are kept (blank) rather than removed so that they still map 1:1 to the layer file's items
      const overlapsPrev = prev && item.textContent === prev.textContent && item.textContent.trim()
        && Math.abs(left - prev.style.left) < height * 0.1 && Math.abs(top - prev.style.top) < height * 0.1;
      if (overlapsPrev) {
        item.textContent = '';
      } else {
        prev = item;
      }
    }
  }

  const accents = _.isEmpty(offsets) ? {} : recoverAccents(pages, offsets, lang);

  // Generate normalized text version without special chars for searching
  _.each(pages, (page, i) => {
    page.index = i;
    page.allText = _.map(page.pageItems, (item,i) => {
      if (offsets[item.style.fontName] && !_.isEmpty(accents) && NON_ASCII.test(item.textContent)) {
        item.textContent = applyAccents(item.textContent, accents);
      }
      let normalizedText = normalize(item.textContent || "", true, false);

      // Some PDFs have the text lines broken in multiple fragments, in the middle of words, and therefore have to be
      // joined without spaces
      let j = i + 1;
      while (page.pageItems[j] && !page.pageItems[j].textContent) j++;
      const nextItem = page.pageItems[j];
      if (normalizedText && nextItem && !isGlued(item, nextItem)) {
        normalizedText += ' ';
      }

      item.normalizedText = normalizedText;

      return item.normalizedText;
    }).join("").replace(/\s+/gi, ' ');
  });
}

// lang ('es', 'pt'...) picks the word list used to recover accented letters; without it all lists are used
export function decodePDF(pages, lang) {
  if (!pages || !pages.length)
    return pages;

  let decodedPages = [];

  let styleStats = {};

  const updateStyleStats = (textContent, { fontName, fontSize, width, height, transform }) => {
    if (!styleStats[fontName]) {
      styleStats[fontName] = { charCount: 0, widthSum: 0, fontSizeSum: 0, count: 0 };
    }
    styleStats[fontName].charCount += textContent.length;
    styleStats[fontName].count += 1;
    styleStats[fontName].widthSum += width / fontSize;
    styleStats[fontName].fontSizeSum += fontSize;
  };

  let lastViewport, lastStyles, lastStyle = {};
  let props = ['fontName', 'fontSize', 'left', 'top', 'width', 'height', 'transform'];

  for (const [viewportC, stylesC, pageItemsC] of pages) {
    let viewport = viewportC === -1 ? lastViewport : (lastViewport = viewportC);

    let styles = stylesC === -1 ? lastStyles : (lastStyles = stylesC);

    let pageItems = [];
    for(const [textContent, styleC] of pageItemsC) {
      let [fontName, fontSize, left, top, width, height, transform] = styleC;

      let style = { fontName, fontSize, left, top, width, height, transform };

      style = _.mapValues(style, (val, prop) => {
        if (val === -1) {
          return (val = lastStyle[prop]);
        } else {
          return (lastStyle[prop] = val);
        }
      });

      updateStyleStats(textContent, style);

      pageItems.push({ textContent, style });
    }

    decodedPages.push({ viewport, styles, pageItems })
  }

  _.each(styleStats, (stat) => {
    stat.avgCharWidth = stat.widthSum / stat.charCount;
    stat.avgFontSize = stat.fontSizeSum / stat.count;
  })


  let globalStyles = generateGlobalStyles(styleStats);

  postProcessText(decodedPages, lang);

  return { pages: decodedPages, globalStyles };
}
